import React, { useState, useRef, Component } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useBusiness } from './BusinessContext';
import { LeadDetailModal, PIPELINE_STAGES, type Lead, type LeadStatus } from './LeadDetailModal';
import { XIcon, SparklesIcon, MessageSquareIcon, TrendingUpIcon, SettingsIcon, FileTextIcon, DollarSignIcon, BotIcon, CheckCircle2Icon, SendIcon, UserIcon, EditIcon, CheckIcon, EyeIcon, ClockIcon, TrashIcon, ArchiveIcon, ToggleRightIcon, UploadCloudIcon, PlusIcon, ImageIcon, MapPinIcon, ExternalLinkIcon, NavigationIcon, SearchIcon, ChevronLeftIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});
interface CatalogItemModalProps {
  onClose: () => void;
  itemTitle?: string;
}
type Tab = 'Overview' | 'Pricing' | 'AI Knowledge' | 'Leads' | 'Activity' | 'Settings';
export function CatalogItemModal({
  onClose,
  itemTitle = 'Toyota Prado GXR 2018'
}: CatalogItemModalProps) {
  const [activeTab, setActiveTab] = useState<Tab>('Overview');
  const tabs: {
    id: Tab;
    icon: React.ReactNode;
  }[] = [{
    id: 'Overview',
    icon: <FileTextIcon className="w-4 h-4" data-id="element-1149" />
  }, {
    id: 'Pricing',
    icon: <DollarSignIcon className="w-4 h-4" data-id="element-1150" />
  }, {
    id: 'AI Knowledge',
    icon: <SparklesIcon className="w-4 h-4" data-id="element-1151" />
  }, {
    id: 'Leads',
    icon: <TrendingUpIcon className="w-4 h-4" data-id="element-1152" />
  }, {
    id: 'Activity',
    icon: <ClockIcon className="w-4 h-4" data-id="element-1153" />
  }, {
    id: 'Settings',
    icon: <SettingsIcon className="w-4 h-4" data-id="element-1154" />
  }];
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!mountNode) return null;
  return createPortal(<AnimatePresence data-id="element-1155">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 28,
      stiffness: 250
    }} className="absolute inset-0 z-[100] bg-[#F8FAFC] flex flex-col overflow-hidden" data-id="element-1156">
        {/* Sticky Mobile Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-100 z-20" data-id="element-1157">
          <div className="flex items-center gap-3 px-4 py-3" data-id="element-1158">
            <button onClick={onClose} className="p-2 -ml-2 text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1159">
              <ChevronLeftIcon className="w-6 h-6" data-id="element-1160" />
            </button>
            <div className="flex-1 min-w-0" data-id="element-1161">
              <div className="flex items-center gap-2" data-id="element-1162">
                <h2 className="text-lg font-bold text-dark-navy truncate" data-id="element-1163">
                  {itemTitle}
                </h2>
                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-[10px] font-bold rounded-full uppercase tracking-wider shrink-0" data-id="element-1164">
                  Active
                </span>
              </div>
              <p className="text-xs text-slate-500 truncate mt-0.5" data-id="element-1165">
                AED 350/day • SUVs
              </p>
            </div>
          </div>

          {/* Horizontal Scrollable Tab Chips */}
          <div className="flex gap-2 overflow-x-auto hide-scrollbar px-4 pb-3" data-id="element-1166">
            {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors shrink-0 ${isActive ? 'bg-primary-indigo text-white shadow-sm' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-1167">
                  <span className={isActive ? 'text-white/80' : 'text-slate-400'} data-id="element-1168">
                    {tab.icon}
                  </span>
                  {tab.id}
                </button>;
          })}
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto relative" data-id="element-1169">
          <div className="p-4 pb-24" data-id="element-1170">
            {activeTab === 'Overview' && <OverviewTab data-id="element-1171" />}
            {activeTab === 'Pricing' && <PricingTab data-id="element-1172" />}
            {activeTab === 'AI Knowledge' && <AIRulesTab data-id="element-1173" />}
            {activeTab === 'Leads' && <LeadsTab itemTitle={itemTitle} data-id="element-1174" />}
            {activeTab === 'Activity' && <ActivityTab data-id="element-1175" />}
            {activeTab === 'Settings' && <SettingsTab onClose={onClose} data-id="element-1176" />}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>, mountNode);
}
const UAE_PLACES = [{
  id: '1',
  name: 'Al Quoz Industrial Area 3',
  city: 'Dubai',
  area: 'Al Quoz',
  lat: 25.1275,
  lng: 55.2289
}, {
  id: '2',
  name: 'Dubai Marina Mall',
  city: 'Dubai',
  area: 'Dubai Marina',
  lat: 25.0768,
  lng: 55.1404
}, {
  id: '3',
  name: 'Jumeirah Beach Residence',
  city: 'Dubai',
  area: 'JBR',
  lat: 25.0776,
  lng: 55.1328
}, {
  id: '4',
  name: 'Business Bay',
  city: 'Dubai',
  area: 'Business Bay',
  lat: 25.1846,
  lng: 55.2672
}, {
  id: '5',
  name: 'Downtown Dubai',
  city: 'Dubai',
  area: 'Downtown',
  lat: 25.1972,
  lng: 55.2744
}, {
  id: '6',
  name: 'Al Maryah Island',
  city: 'Abu Dhabi',
  area: 'Al Maryah',
  lat: 24.5019,
  lng: 54.3905
}, {
  id: '7',
  name: 'Yas Island',
  city: 'Abu Dhabi',
  area: 'Yas Island',
  lat: 24.4961,
  lng: 54.6039
}, {
  id: '8',
  name: 'Al Majaz Waterfront',
  city: 'Sharjah',
  area: 'Al Majaz',
  lat: 25.3216,
  lng: 55.3855
}];
function MapEvents({
  onLocationSelect
}: {
  onLocationSelect: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    }
  });
  return null;
}
function OverviewTab() {
  const [isEditing, setIsEditing] = useState(false);
  const [images, setImages] = useState(['https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800', 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800&sig=1', 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800&sig=2', 'https://images.unsplash.com/photo-1550133730-695473e544be?auto=format&fit=crop&q=80&w=800&sig=3']);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [details, setDetails] = useState({
    title: 'Toyota Prado GXR 2023',
    price: '350',
    status: 'Available',
    description: 'Premium SUV perfect for family trips and desert adventures. Fully insured and maintained. Features include leather seats, sunroof, advanced navigation, and 4WD capabilities.',
    specs: {
      Category: 'SUVs',
      Make: 'Toyota',
      Model: 'Prado GXR',
      Year: '2023',
      Seats: '7',
      Transmission: 'Automatic',
      'Fuel Type': 'Petrol'
    }
  });
  const [location, setLocation] = useState({
    address: 'Al Quoz Industrial Area 3, Dubai, UAE',
    city: 'Dubai',
    area: 'Al Quoz',
    lat: 25.1275,
    lng: 55.2289
  });
  const [draftLocation, setDraftLocation] = useState(location);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const filteredPlaces = UAE_PLACES.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.city.toLowerCase().includes(searchQuery.toLowerCase()) || p.area.toLowerCase().includes(searchQuery.toLowerCase()));
  const handlePlaceSelect = (place: (typeof UAE_PLACES)[0]) => {
    setDraftLocation({
      address: `${place.name}, ${place.city}, UAE`,
      city: place.city,
      area: place.area,
      lat: place.lat,
      lng: place.lng
    });
    setSearchQuery(place.name);
    setShowSuggestions(false);
  };
  const handleMapClick = (lat: number, lng: number) => {
    const nearest = UAE_PLACES.reduce((closest, place) => {
      const dist = (place.lat - lat) ** 2 + (place.lng - lng) ** 2;
      const closestDist = (closest.lat - lat) ** 2 + (closest.lng - lng) ** 2;
      return dist < closestDist ? place : closest;
    }, UAE_PLACES[0]);
    setDraftLocation({
      address: `Near ${nearest.area}, ${nearest.city}, UAE`,
      city: nearest.city,
      area: nearest.area,
      lat,
      lng
    });
    setSearchQuery('');
  };
  const saveLocation = () => {
    setLocation(draftLocation);
  };
  const mapQuery = location.address || `${location.area} ${location.city}`;
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const newImages = [...images, images[0]];
      setImages(newImages);
      setActiveImageIndex(newImages.length - 1);
    }
  };
  const handleDeleteImage = () => {
    if (images.length > 1) {
      const newImages = images.filter((_, idx) => idx !== activeImageIndex);
      setImages(newImages);
      setActiveImageIndex(Math.max(0, activeImageIndex - 1));
    }
  };
  return <div className="flex flex-col gap-6" data-id="element-1177">
      {/* Gallery Section */}
      <div className="flex flex-col gap-3" data-id="element-1178">
        <div className="w-full aspect-[4/3] bg-slate-100 rounded-2xl overflow-hidden relative shadow-sm border border-slate-200" data-id="element-1179">
          <AnimatePresence mode="wait" data-id="element-1180">
            <motion.img key={activeImageIndex} src={images[activeImageIndex]} alt="Main" className="w-full h-full object-cover" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2
          }} data-id="element-1181" />
          </AnimatePresence>

          {/* Always visible image controls on mobile */}
          <div className="absolute bottom-3 right-3 flex gap-2" data-id="element-1182">
            <button className="px-3 py-1.5 bg-white/90 backdrop-blur-md text-slate-700 text-xs font-bold rounded-lg shadow-sm flex items-center gap-1.5 active:scale-95 transition-transform" onClick={() => fileInputRef.current?.click()} data-id="element-1183">
              <UploadCloudIcon className="w-3.5 h-3.5" data-id="element-1184" /> Replace
            </button>
            {images.length > 1 && <button className="p-1.5 bg-white/90 backdrop-blur-md text-red-600 rounded-lg shadow-sm active:scale-95 transition-transform" onClick={handleDeleteImage} data-id="element-1185">
                <TrashIcon className="w-4 h-4" data-id="element-1186" />
              </button>}
          </div>
        </div>

        {/* Thumbnail Rail */}
        <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1" data-id="element-1187">
          {images.map((img, idx) => <button key={idx} onClick={() => setActiveImageIndex(idx)} className={`w-20 h-16 rounded-xl flex-shrink-0 overflow-hidden border-2 transition-all ${idx === activeImageIndex ? 'border-primary-indigo shadow-sm' : 'border-transparent opacity-70'}`} data-id="element-1188">
              <img src={img} alt={`Thumb ${idx}`} className="w-full h-full object-cover" data-id="element-1189" />
            </button>)}
          <button className="w-20 h-16 rounded-xl flex-shrink-0 border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-500 bg-slate-50 active:bg-slate-100" onClick={() => fileInputRef.current?.click()} data-id="element-1190">
            <PlusIcon className="w-5 h-5 mb-0.5" data-id="element-1191" />
            <span className="text-[10px] font-bold uppercase tracking-wider" data-id="element-1192">
              Add
            </span>
          </button>
        </div>
        <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleImageUpload} data-id="element-1193" />
      </div>

      {isEditing ? <div className="flex flex-col gap-6" data-id="element-1194">
          <div className="space-y-4" data-id="element-1195">
            <div data-id="element-1196">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1197">
                Title
              </label>
              <input type="text" value={details.title} onChange={e => setDetails({
            ...details,
            title: e.target.value
          })} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none text-sm" data-id="element-1198" />
            </div>
            <div data-id="element-1199">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1200">
                Price (AED/day)
              </label>
              <div className="relative" data-id="element-1201">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-sm" data-id="element-1202">
                  AED
                </span>
                <input type="text" value={details.price} onChange={e => setDetails({
              ...details,
              price: e.target.value
            })} className="w-full pl-12 pr-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none text-sm" data-id="element-1203" />
              </div>
            </div>
            <div data-id="element-1204">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1205">
                Status
              </label>
              <select value={details.status} onChange={e => setDetails({
            ...details,
            status: e.target.value
          })} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none text-sm appearance-none" data-id="element-1206">
                <option value="Available" data-id="element-1207">Available</option>
                <option value="Rented" data-id="element-1208">Rented</option>
                <option value="Maintenance" data-id="element-1209">Maintenance</option>
              </select>
            </div>
            <div data-id="element-1210">
              <label className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-1211">
                Description
              </label>
              <textarea rows={4} value={details.description} onChange={e => setDetails({
            ...details,
            description: e.target.value
          })} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none resize-none text-sm leading-relaxed" data-id="element-1212" />
            </div>
          </div>

          <div data-id="element-1213">
            <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-1214">
              Key Specifications
            </h4>
            <div className="grid grid-cols-2 gap-3" data-id="element-1215">
              {Object.entries(details.specs).map(([key, val]) => <div key={key} className="bg-white p-3 rounded-xl border border-slate-200 shadow-sm" data-id="element-1216">
                  <label className="block text-xs font-medium text-slate-500 mb-1.5" data-id="element-1217">
                    {key}
                  </label>
                  <input type="text" value={val} onChange={e => setDetails({
              ...details,
              specs: {
                ...details.specs,
                [key]: e.target.value
              }
            })} className="w-full px-2.5 py-1.5 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none text-sm" data-id="element-1218" />
                </div>)}
            </div>
          </div>

          <div data-id="element-1219">
            <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-1220">
              Product Location
            </h4>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm flex flex-col" data-id="element-1221">
              <div className="p-3 border-b border-slate-100 bg-slate-50" data-id="element-1222">
                <div className="relative" data-id="element-1223">
                  <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-1224" />
                  <input value={searchQuery} onChange={e => {
                setSearchQuery(e.target.value);
                setShowSuggestions(true);
              }} onFocus={() => setShowSuggestions(true)} placeholder="Search places in UAE..." className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none text-sm" data-id="element-1225" />
                  {showSuggestions && searchQuery && filteredPlaces.length > 0 && <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto" data-id="element-1226">
                        {filteredPlaces.map(place => <button key={place.id} onClick={() => handlePlaceSelect(place)} className="w-full text-left px-4 py-2.5 active:bg-slate-50 border-b border-slate-50 last:border-0 flex items-center gap-3" data-id="element-1227">
                            <MapPinIcon className="w-4 h-4 text-slate-400 shrink-0" data-id="element-1228" />
                            <div data-id="element-1229">
                              <div className="text-sm font-medium text-slate-900" data-id="element-1230">
                                {place.name}
                              </div>
                              <div className="text-xs text-slate-500" data-id="element-1231">
                                {place.area}, {place.city}
                              </div>
                            </div>
                          </button>)}
                      </div>}
                </div>
              </div>
              <div className="h-48 relative z-10" data-id="element-1232">
                <MapContainer center={[draftLocation.lat, draftLocation.lng]} zoom={13} scrollWheelZoom={false} className="w-full h-full" data-id="element-1233">
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1234" />
                  <Marker position={[draftLocation.lat, draftLocation.lng]} data-id="element-1235" />
                  <MapEvents onLocationSelect={handleMapClick} data-id="element-1236" />
                </MapContainer>
              </div>
              <div className="p-3 bg-white flex flex-col gap-3" data-id="element-1237">
                <div data-id="element-1238">
                  <div className="text-sm font-semibold text-slate-900" data-id="element-1239">
                    {draftLocation.address}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-1240">
                    {draftLocation.lat.toFixed(4)},{' '}
                    {draftLocation.lng.toFixed(4)}
                  </div>
                </div>
                <button onClick={saveLocation} className="w-full py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl active:scale-95 transition-transform flex items-center justify-center gap-2" data-id="element-1241">
                  <CheckIcon className="w-4 h-4" data-id="element-1242" /> Save Location
                </button>
              </div>
            </div>
          </div>

          {/* Sticky Bottom Action Bar for Edit Mode */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-100 shadow-[0_-8px_30px_rgba(0,0,0,0.04)] z-30 flex gap-3" data-id="element-1243">
            <button onClick={() => setIsEditing(false)} className="flex-1 py-3.5 bg-slate-100 text-slate-700 text-sm font-bold rounded-xl active:scale-95 transition-transform" data-id="element-1244">
              Cancel
            </button>
            <button onClick={() => setIsEditing(false)} className="flex-[2] py-3.5 bg-primary-indigo text-white text-sm font-bold rounded-xl active:scale-95 transition-transform shadow-sm" data-id="element-1245">
              Save Changes
            </button>
          </div>
        </div> : <div className="flex flex-col gap-6" data-id="element-1246">
          {/* Title & Price */}
          <div data-id="element-1247">
            <div className="flex items-center justify-between mb-2" data-id="element-1248">
              <span className="px-2.5 py-1 bg-green-50 border border-green-200 text-green-700 text-[10px] font-bold uppercase tracking-wider rounded-lg shadow-sm" data-id="element-1249">
                {details.status}
              </span>
              <button onClick={() => setIsEditing(true)} className="p-2 bg-white border border-slate-200 rounded-full shadow-sm text-slate-600 active:bg-slate-50" data-id="element-1250">
                <EditIcon className="w-4 h-4" data-id="element-1251" />
              </button>
            </div>
            <h3 className="text-2xl font-display font-bold text-dark-navy leading-tight mb-2" data-id="element-1252">
              {details.title}
            </h3>
            <div className="flex items-baseline gap-1" data-id="element-1253">
              <span className="text-2xl font-bold text-primary-indigo tracking-tight" data-id="element-1254">
                <span className="text-sm text-primary-indigo/70 font-semibold mr-1" data-id="element-1255">
                  AED
                </span>
                {details.price}
              </span>
              <span className="text-sm font-medium text-slate-500" data-id="element-1256">/day</span>
            </div>
          </div>

          {/* AI Readiness Card */}
          <div className="bg-purple-50 border border-purple-100 rounded-2xl p-4 shadow-sm flex flex-col gap-3" data-id="element-1257">
            <div className="flex items-center gap-3" data-id="element-1258">
              <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center shrink-0" data-id="element-1259">
                <SparklesIcon className="w-5 h-5 text-ai-purple" data-id="element-1260" />
              </div>
              <div data-id="element-1261">
                <h4 className="font-bold text-slate-900 text-sm" data-id="element-1262">
                  AI Readiness Score
                </h4>
                <p className="text-xs text-slate-500 mt-0.5" data-id="element-1263">
                  Optimized for AI matching
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-1264">
              <div className="flex-1 h-2.5 bg-white rounded-full overflow-hidden shadow-inner" data-id="element-1265">
                <div className="h-full bg-ai-purple rounded-full w-[92%]" data-id="element-1266" />
              </div>
              <span className="font-bold text-ai-purple text-sm" data-id="element-1267">92%</span>
            </div>
          </div>

          {/* Description */}
          <div data-id="element-1268">
            <h4 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-1269">
              <FileTextIcon className="w-4 h-4 text-slate-400" data-id="element-1270" /> Description
            </h4>
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-id="element-1271">
              <p className="text-sm text-slate-600 leading-relaxed" data-id="element-1272">
                {details.description}
              </p>
            </div>
          </div>

          {/* Key Specs */}
          <div data-id="element-1273">
            <h4 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-1274">
              <SettingsIcon className="w-4 h-4 text-slate-400" data-id="element-1275" /> Key
              Specifications
            </h4>
            <div className="grid grid-cols-2 gap-3" data-id="element-1276">
              {Object.entries(details.specs).map(([key, val]) => <div key={key} className="bg-white border border-slate-200 rounded-xl p-3 shadow-sm" data-id="element-1277">
                  <span className="block text-[11px] font-medium text-slate-500 mb-0.5" data-id="element-1278">
                    {key}
                  </span>
                  <span className="block font-semibold text-slate-900 text-sm truncate" data-id="element-1279">
                    {val}
                  </span>
                </div>)}
            </div>
          </div>

          {/* Location */}
          <div data-id="element-1280">
            <h4 className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-2" data-id="element-1281">
              <MapPinIcon className="w-4 h-4 text-slate-400" data-id="element-1282" /> Location
            </h4>
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm flex flex-col" data-id="element-1283">
              <div className="h-40 relative z-10" data-id="element-1284">
                <MapContainer center={[location.lat, location.lng]} zoom={14} scrollWheelZoom={false} className="w-full h-full" data-id="element-1285">
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" data-id="element-1286" />
                  <Marker position={[location.lat, location.lng]} data-id="element-1287" />
                </MapContainer>
              </div>
              <div className="p-4 flex flex-col gap-3" data-id="element-1288">
                <div data-id="element-1289">
                  <p className="text-sm font-semibold text-slate-900" data-id="element-1290">
                    {location.area}, {location.city}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5" data-id="element-1291">
                    {location.address}
                  </p>
                </div>
                <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapQuery)}`} target="_blank" rel="noreferrer" className="w-full py-2.5 bg-slate-50 border border-slate-200 text-slate-700 text-sm font-bold rounded-xl active:bg-slate-100 flex items-center justify-center gap-2" data-id="element-1292">
                  <ExternalLinkIcon className="w-4 h-4" data-id="element-1293" /> Open in Maps
                </a>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}
interface DeliveryRow {
  id: number;
  location: string;
  charge: string;
}
interface AddonRow {
  id: number;
  item: string;
  type: string;
  amount: string;
}
function PricingTab() {
  const [isEditing, setIsEditing] = useState(false);
  const [base, setBase] = useState({
    daily: '350',
    weekly: '2100',
    monthly: '7500'
  });
  const [delivery, setDelivery] = useState<DeliveryRow[]>([{
    id: 1,
    location: 'Inside Dubai',
    charge: '50'
  }, {
    id: 2,
    location: 'Sharjah',
    charge: '100'
  }, {
    id: 3,
    location: 'Airport Delivery',
    charge: '75'
  }]);
  const [addons, setAddons] = useState<AddonRow[]>([{
    id: 1,
    item: 'Security Deposit',
    type: 'Refundable',
    amount: '1000'
  }, {
    id: 2,
    item: 'Additional Driver',
    type: 'Per Day',
    amount: '25'
  }, {
    id: 3,
    item: 'Child Seat',
    type: 'Fixed',
    amount: '50'
  }]);
  const num = (v: string) => parseFloat(v.replace(/,/g, '')) || 0;
  const fmt = (v: number) => v.toLocaleString('en-US');
  const weeklyDiscount = Math.max(0, Math.round((1 - num(base.weekly) / (num(base.daily) * 7)) * 100));
  const monthlyDiscount = Math.max(0, Math.round((1 - num(base.monthly) / (num(base.daily) * 30)) * 100));
  const updateDelivery = (id: number, field: keyof DeliveryRow, val: string) => setDelivery(prev => prev.map(r => r.id === id ? {
    ...r,
    [field]: val
  } : r));
  const addDelivery = () => setDelivery(prev => [...prev, {
    id: Date.now(),
    location: 'New location',
    charge: '0'
  }]);
  const removeDelivery = (id: number) => setDelivery(prev => prev.filter(r => r.id !== id));
  const updateAddon = (id: number, field: keyof AddonRow, val: string) => setAddons(prev => prev.map(r => r.id === id ? {
    ...r,
    [field]: val
  } : r));
  const addAddon = () => setAddons(prev => [...prev, {
    id: Date.now(),
    item: 'New add-on',
    type: 'Fixed',
    amount: '0'
  }]);
  const removeAddon = (id: number) => setAddons(prev => prev.filter(r => r.id !== id));
  const baseCards = [{
    key: 'daily' as const,
    label: 'Daily Rate',
    discount: 0
  }, {
    key: 'weekly' as const,
    label: 'Weekly Rate',
    discount: weeklyDiscount
  }, {
    key: 'monthly' as const,
    label: 'Monthly Rate',
    discount: monthlyDiscount
  }];
  return <div className="flex flex-col gap-6" data-id="element-1294">
      <div className="flex items-center justify-between" data-id="element-1295">
        <div data-id="element-1296">
          <h3 className="text-lg font-bold text-dark-navy flex items-center gap-2" data-id="element-1297">
            <DollarSignIcon className="w-5 h-5 text-primary-indigo" data-id="element-1298" /> Pricing
          </h3>
          <p className="text-xs text-slate-500 mt-1" data-id="element-1299">
            Rates, charges and add-ons.
          </p>
        </div>
        {!isEditing && <button onClick={() => setIsEditing(true)} className="p-2 bg-white border border-slate-200 rounded-full shadow-sm text-slate-600 active:bg-slate-50" data-id="element-1300">
            <EditIcon className="w-4 h-4" data-id="element-1301" />
          </button>}
      </div>

      <section data-id="element-1302">
        <h4 className="text-sm font-bold text-slate-900 mb-3" data-id="element-1303">Base Pricing</h4>
        <div className="flex flex-col gap-3" data-id="element-1304">
          {baseCards.map(card => <div key={card.key} className="p-4 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col gap-2" data-id="element-1305">
              <div className="flex items-center justify-between" data-id="element-1306">
                <div className="text-sm font-medium text-slate-500" data-id="element-1307">
                  {card.label}
                </div>
                {card.discount > 0 && <span className="text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-md" data-id="element-1308">
                    Save {card.discount}%
                  </span>}
              </div>
              {isEditing ? <div className="relative" data-id="element-1309">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium" data-id="element-1310">
                    AED
                  </span>
                  <input type="text" value={base[card.key]} onChange={e => setBase({
              ...base,
              [card.key]: e.target.value
            })} className="w-full pl-12 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-base font-bold text-dark-navy focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo outline-none" data-id="element-1311" />
                </div> : <div className="text-xl font-bold text-dark-navy" data-id="element-1312">
                  AED {fmt(num(base[card.key]))}
                </div>}
            </div>)}
        </div>
      </section>

      <section data-id="element-1313">
        <div className="flex items-center justify-between mb-3" data-id="element-1314">
          <h4 className="text-sm font-bold text-slate-900" data-id="element-1315">
            Delivery & Pickup
          </h4>
          {isEditing && <button onClick={addDelivery} className="text-xs font-bold text-primary-indigo flex items-center gap-1 bg-indigo-50 px-2.5 py-1.5 rounded-lg active:scale-95" data-id="element-1316">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-1317" /> Add
            </button>}
        </div>
        <div className="flex flex-col gap-3" data-id="element-1318">
          {delivery.map(row => <div key={row.id} className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col gap-3" data-id="element-1319">
              {isEditing ? <>
                  <div data-id="element-1320">
                    <label className="text-[11px] font-semibold text-slate-500 mb-1 block" data-id="element-1321">
                      Location
                    </label>
                    <input type="text" value={row.location} onChange={e => updateDelivery(row.id, 'location', e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-indigo/20 outline-none" data-id="element-1322" />
                  </div>
                  <div data-id="element-1323">
                    <label className="text-[11px] font-semibold text-slate-500 mb-1 block" data-id="element-1324">
                      Charge (AED)
                    </label>
                    <input type="text" value={row.charge} onChange={e => updateDelivery(row.id, 'charge', e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-primary-indigo/20 outline-none" data-id="element-1325" />
                  </div>
                  <button onClick={() => removeDelivery(row.id)} className="w-full py-2 mt-1 text-sm text-red-600 font-bold bg-red-50 rounded-lg active:bg-red-100 flex items-center justify-center gap-1" data-id="element-1326">
                    <TrashIcon className="w-4 h-4" data-id="element-1327" /> Remove
                  </button>
                </> : <div className="flex items-center justify-between" data-id="element-1328">
                  <div className="text-sm font-medium text-dark-navy" data-id="element-1329">
                    {row.location}
                  </div>
                  <div className="text-sm font-bold text-primary-indigo" data-id="element-1330">
                    AED {fmt(num(row.charge))}
                  </div>
                </div>}
            </div>)}
        </div>
      </section>

      <section data-id="element-1331">
        <div className="flex items-center justify-between mb-3" data-id="element-1332">
          <h4 className="text-sm font-bold text-slate-900" data-id="element-1333">
            Add-ons & Deposits
          </h4>
          {isEditing && <button onClick={addAddon} className="text-xs font-bold text-primary-indigo flex items-center gap-1 bg-indigo-50 px-2.5 py-1.5 rounded-lg active:scale-95" data-id="element-1334">
              <PlusIcon className="w-3.5 h-3.5" data-id="element-1335" /> Add
            </button>}
        </div>
        <div className="flex flex-col gap-3" data-id="element-1336">
          {addons.map(row => <div key={row.id} className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col gap-3" data-id="element-1337">
              {isEditing ? <>
                  <div data-id="element-1338">
                    <label className="text-[11px] font-semibold text-slate-500 mb-1 block" data-id="element-1339">
                      Item
                    </label>
                    <input type="text" value={row.item} onChange={e => updateAddon(row.id, 'item', e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-indigo/20 outline-none" data-id="element-1340" />
                  </div>
                  <div className="grid grid-cols-2 gap-3" data-id="element-1341">
                    <div data-id="element-1342">
                      <label className="text-[11px] font-semibold text-slate-500 mb-1 block" data-id="element-1343">
                        Type
                      </label>
                      <select value={row.type} onChange={e => updateAddon(row.id, 'type', e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-indigo/20 outline-none appearance-none" data-id="element-1344">
                        <option value="Refundable" data-id="element-1345">Refundable</option>
                        <option value="Per Day" data-id="element-1346">Per Day</option>
                        <option value="Fixed" data-id="element-1347">Fixed</option>
                      </select>
                    </div>
                    <div data-id="element-1348">
                      <label className="text-[11px] font-semibold text-slate-500 mb-1 block" data-id="element-1349">
                        Amount (AED)
                      </label>
                      <input type="text" value={row.amount} onChange={e => updateAddon(row.id, 'amount', e.target.value)} className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-medium focus:ring-2 focus:ring-primary-indigo/20 outline-none" data-id="element-1350" />
                    </div>
                  </div>
                  <button onClick={() => removeAddon(row.id)} className="w-full py-2 mt-1 text-sm text-red-600 font-bold bg-red-50 rounded-lg active:bg-red-100 flex items-center justify-center gap-1" data-id="element-1351">
                    <TrashIcon className="w-4 h-4" data-id="element-1352" /> Remove
                  </button>
                </> : <div className="flex flex-col gap-1" data-id="element-1353">
                  <div className="flex items-center justify-between" data-id="element-1354">
                    <div className="text-sm font-medium text-dark-navy" data-id="element-1355">
                      {row.item}
                    </div>
                    <div className="text-sm font-bold text-primary-indigo" data-id="element-1356">
                      AED {fmt(num(row.amount))}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-1357">{row.type}</div>
                </div>}
            </div>)}
        </div>
      </section>

      {isEditing && <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-slate-100 shadow-[0_-8px_30px_rgba(0,0,0,0.04)] z-30 flex gap-3" data-id="element-1358">
          <button onClick={() => setIsEditing(false)} className="flex-1 py-3.5 bg-slate-100 text-slate-700 text-sm font-bold rounded-xl active:scale-95 transition-transform" data-id="element-1359">
            Cancel
          </button>
          <button onClick={() => setIsEditing(false)} className="flex-[2] py-3.5 bg-primary-indigo text-white text-sm font-bold rounded-xl active:scale-95 transition-transform shadow-sm" data-id="element-1360">
            Save Pricing
          </button>
        </div>}
    </div>;
}
function leadStatusBadge(status: LeadStatus) {
  switch (status) {
    case 'Won':
    case 'Ready To Buy':
      return 'bg-green-100 text-green-700';
    case 'Qualified':
      return 'bg-indigo-100 text-primary-indigo';
    case 'Negotiating':
      return 'bg-blue-100 text-blue-700';
    case 'Interested':
      return 'bg-amber-100 text-amber-700';
    case 'Lost':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-slate-100 text-slate-600';
  }
}
function LeadsTab({
  itemTitle
}: {
  itemTitle: string;
}) {
  const [leads, setLeads] = useState<Lead[]>([{
    name: 'Ahmed Al Mansoori',
    intentScore: 94,
    lookingFor: 'Toyota Prado',
    budget: 'AED 90,000',
    timeline: 'Within 7 Days',
    location: 'Dubai',
    matchedItem: itemTitle,
    status: 'Qualified',
    summary: 'Customer strongly prefers GCC vehicles. Ready to purchase this week.',
    phone: '+971 50 123 4567',
    email: 'ahmed@email.com',
    preferredContact: 'WhatsApp',
    preferredLanguage: 'Arabic',
    source: 'Talab AI',
    lastActive: '12m ago',
    receivedDate: 'Jun 6, 2026',
    owner: 'Sara',
    nextAction: 'Call customer',
    nextActionDue: 'Today',
    consent: 'shared'
  }, {
    name: 'Omar Haddad',
    intentScore: 67,
    lookingFor: 'Family SUV',
    budget: 'AED 70,000 - 85,000',
    timeline: 'This Month',
    location: 'Sharjah',
    matchedItem: itemTitle,
    status: 'Interested',
    summary: 'Exploring options for a family vehicle. Requested availability confirmation.',
    source: 'Talab AI',
    lastActive: '3h ago',
    receivedDate: 'Jun 5, 2026',
    owner: 'Unassigned',
    nextAction: 'Follow up tomorrow',
    nextActionDue: 'Tomorrow',
    consent: 'requested'
  }]);
  const [statusFilter, setStatusFilter] = useState<'All' | LeadStatus>('All');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const filtered = statusFilter === 'All' ? leads : leads.filter(l => l.status === statusFilter);
  const updateStatus = (status: LeadStatus) => setSelectedLead(cur => {
    if (!cur) return cur;
    setLeads(prev => prev.map(l => l.name === cur.name ? {
      ...l,
      status
    } : l));
    return {
      ...cur,
      status
    };
  });
  const updateOwner = (owner: string) => setSelectedLead(cur => {
    if (!cur) return cur;
    setLeads(prev => prev.map(l => l.name === cur.name ? {
      ...l,
      owner
    } : l));
    return {
      ...cur,
      owner
    };
  });
  return <div className="flex flex-col gap-5" data-id="element-1361">
      <div data-id="element-1362">
        <h3 className="text-lg font-bold text-dark-navy" data-id="element-1363">Leads</h3>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-1364">
          {leads.length} buyers matched.
        </p>
      </div>

      <select value={statusFilter} onChange={e => setStatusFilter(e.target.value as 'All' | LeadStatus)} className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary-indigo/20 appearance-none shadow-sm" data-id="element-1365">
        <option value="All" data-id="element-1366">All Stages</option>
        {PIPELINE_STAGES.map(s => <option key={s} value={s} data-id="element-1367">
            {s}
          </option>)}
      </select>

      <div className="flex flex-col gap-3" data-id="element-1368">
        {filtered.map((lead, i) => <div key={i} onClick={() => setSelectedLead(lead)} className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm active:scale-[0.98] transition-transform flex flex-col gap-3" data-id="element-1369">
            <div className="flex justify-between items-start" data-id="element-1370">
              <div className="flex items-center gap-3" data-id="element-1371">
                <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600 shrink-0" data-id="element-1372">
                  {lead.name.charAt(0)}
                </div>
                <div data-id="element-1373">
                  <div className="font-bold text-dark-navy text-sm" data-id="element-1374">
                    {lead.name}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5" data-id="element-1375">
                    Active {lead.lastActive}
                  </div>
                </div>
              </div>
              <span className={`px-2 py-1 text-[10px] font-bold rounded-full uppercase tracking-wider ${leadStatusBadge(lead.status)}`} data-id="element-1376">
                {lead.status}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-slate-50 p-2.5 rounded-xl" data-id="element-1377">
              <div className="flex-1 min-w-0" data-id="element-1378">
                <div className="text-[10px] font-semibold text-slate-400 uppercase" data-id="element-1379">
                  Looking for
                </div>
                <div className="text-xs font-medium text-slate-700 truncate" data-id="element-1380">
                  {lead.lookingFor}
                </div>
              </div>
              <div className="w-px h-6 bg-slate-200" data-id="element-1381" />
              <div className="flex-1 min-w-0 pl-2" data-id="element-1382">
                <div className="text-[10px] font-semibold text-slate-400 uppercase" data-id="element-1383">
                  Intent
                </div>
                <div className={`text-xs font-bold ${lead.intentScore >= 80 ? 'text-green-600' : 'text-amber-600'}`} data-id="element-1384">
                  {lead.intentScore}/100
                </div>
              </div>
            </div>
          </div>)}
        {filtered.length === 0 && <div className="py-10 text-center text-sm text-slate-400 bg-white border border-dashed border-slate-200 rounded-2xl" data-id="element-1385">
            No leads in this stage yet.
          </div>}
      </div>

      {selectedLead && <LeadDetailModal lead={selectedLead} onClose={() => setSelectedLead(null)} onStatusChange={updateStatus} onOwnerChange={updateOwner} data-id="element-1386" />}
    </div>;
}
function ActivityTab() {
  return <div className="flex flex-col gap-5" data-id="element-1387">
      <h3 className="text-lg font-bold text-dark-navy" data-id="element-1388">Item Activity</h3>
      <div className="relative border-l-2 border-slate-200 ml-4 space-y-6 pb-4" data-id="element-1389">
        <div className="relative pl-6" data-id="element-1390">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-blue-100 border-2 border-white flex items-center justify-center" data-id="element-1391">
            <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-1392" />
          </div>
          <div className="text-xs font-semibold text-slate-500 mb-0.5" data-id="element-1393">
            Today, 2:45 PM
          </div>
          <div className="font-bold text-dark-navy text-sm" data-id="element-1394">Price updated</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1395">
            Base price changed from AED 320 to AED 350.
          </div>
        </div>
        <div className="relative pl-6" data-id="element-1396">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-purple-100 border-2 border-white flex items-center justify-center" data-id="element-1397">
            <div className="w-2 h-2 rounded-full bg-ai-purple" data-id="element-1398" />
          </div>
          <div className="text-xs font-semibold text-slate-500 mb-0.5" data-id="element-1399">
            Yesterday, 11:20 AM
          </div>
          <div className="font-bold text-dark-navy text-sm" data-id="element-1400">AI Rule added</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1401">
            Added rule: "Airport delivery AED 75".
          </div>
        </div>
        <div className="relative pl-6" data-id="element-1402">
          <div className="absolute -left-[9px] top-1 w-4 h-4 rounded-full bg-green-100 border-2 border-white flex items-center justify-center" data-id="element-1403">
            <div className="w-2 h-2 rounded-full bg-green-500" data-id="element-1404" />
          </div>
          <div className="text-xs font-semibold text-slate-500 mb-0.5" data-id="element-1405">
            Oct 12, 09:00 AM
          </div>
          <div className="font-bold text-dark-navy text-sm" data-id="element-1406">Item published</div>
          <div className="text-sm text-slate-600 mt-1" data-id="element-1407">
            Item moved from Draft to Active.
          </div>
        </div>
      </div>
    </div>;
}
function SettingsTab({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    trialActive,
    startTrial
  } = useBusiness();
  const VALIDITY_TOTAL = 30;
  const [published, setPublished] = useState(true);
  const [hideFromSearch, setHideFromSearch] = useState(false);
  const [archived, setArchived] = useState(false);
  const [daysRemaining, setDaysRemaining] = useState(12);
  const [showUnpublish, setShowUnpublish] = useState(false);
  const [unpublishReason, setUnpublishReason] = useState('sold');
  const [otherReason, setOtherReason] = useState('');
  const [showTrialPrompt, setShowTrialPrompt] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [itemCategory, setItemCategory] = useState('SUVs');
  const [flash, setFlash] = useState<string | null>(null);
  const notify = (msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash(null), 2500);
  };
  const handlePublishToggle = () => {
    if (published) {
      setShowUnpublish(v => !v);
    } else {
      if (!trialActive) {
        setShowTrialPrompt(true);
      } else {
        setPublished(true);
        notify('Item published.');
      }
    }
  };
  const confirmUnpublish = () => {
    setPublished(false);
    setShowUnpublish(false);
    notify('Item unpublished.');
  };
  const toggleArchive = () => {
    const next = !archived;
    setArchived(next);
    if (next) setPublished(false);
    notify(next ? 'Item archived.' : 'Item restored.');
  };
  return <div className="flex flex-col gap-6" data-id="element-1408">
      <AnimatePresence data-id="element-1409">
        {flash && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} className="fixed top-20 left-4 right-4 z-50 flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 text-green-800 text-sm font-bold rounded-xl shadow-lg" data-id="element-1410">
            <CheckCircle2Icon className="w-5 h-5 text-green-600" data-id="element-1411" />
            {flash}
          </motion.div>}
      </AnimatePresence>

      <section data-id="element-1412">
        <h3 className="text-lg font-bold text-dark-navy mb-3" data-id="element-1413">
          Classification
        </h3>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-id="element-1414">
          <label className="block text-xs font-semibold text-slate-500 mb-2" data-id="element-1415">
            Category
          </label>
          <select value={itemCategory} onChange={e => {
          setItemCategory(e.target.value);
          notify(`Category set to ${e.target.value}.`);
        }} className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-indigo/20 text-sm outline-none appearance-none font-medium" data-id="element-1416">
            <option value="SUVs" data-id="element-1417">SUVs</option>
            <option value="Sedans" data-id="element-1418">Sedans</option>
            <option value="Hatchbacks" data-id="element-1419">Hatchbacks</option>
            <option value="Luxury" data-id="element-1420">Luxury</option>
          </select>
        </div>
      </section>

      <section data-id="element-1421">
        <h3 className="text-lg font-bold text-dark-navy mb-3" data-id="element-1422">
          Listing Validity
        </h3>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm" data-id="element-1423">
          <div className="flex items-center justify-between mb-3" data-id="element-1424">
            <div className="font-semibold text-dark-navy text-sm" data-id="element-1425">
              Days Remaining
            </div>
            <div className="font-bold text-primary-indigo text-sm" data-id="element-1426">
              {daysRemaining} days
            </div>
          </div>
          <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden mb-4" data-id="element-1427">
            <motion.div className="h-full bg-primary-indigo" animate={{
            width: `${Math.round(daysRemaining / VALIDITY_TOTAL * 100)}%`
          }} data-id="element-1428" />
          </div>
          <button onClick={() => {
          setDaysRemaining(d => Math.min(VALIDITY_TOTAL, d + 30));
          notify('Validity extended.');
        }} disabled={daysRemaining >= VALIDITY_TOTAL} className="w-full py-3 bg-slate-50 border border-slate-200 text-slate-700 text-sm font-bold rounded-xl active:bg-slate-100 disabled:opacity-50" data-id="element-1429">
            Extend Validity (+30 days)
          </button>
        </div>
      </section>

      <section data-id="element-1430">
        <h3 className="text-lg font-bold text-dark-navy mb-3" data-id="element-1431">Visibility</h3>
        <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col gap-4" data-id="element-1432">
          <div className="flex items-center justify-between" data-id="element-1433">
            <div data-id="element-1434">
              <div className="font-semibold text-dark-navy text-sm flex items-center gap-2" data-id="element-1435">
                Published
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-md ${published ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-1436">
                  {published ? 'Live' : 'Hidden'}
                </span>
              </div>
            </div>
            <button onClick={handlePublishToggle} data-id="element-1437">
              <ToggleRightIcon className={`w-10 h-10 transition-colors ${published ? 'text-green-500' : 'text-slate-300 rotate-180'}`} data-id="element-1438" />
            </button>
          </div>

          <AnimatePresence data-id="element-1439">
            {showUnpublish && published && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="overflow-hidden" data-id="element-1440">
                <div className="pt-3 border-t border-slate-100 flex flex-col gap-3" data-id="element-1441">
                  <select value={unpublishReason} onChange={e => setUnpublishReason(e.target.value)} className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none appearance-none" data-id="element-1442">
                    <option value="sold" data-id="element-1443">Sold</option>
                    <option value="not_for_sale" data-id="element-1444">Not for sale anymore</option>
                    <option value="other" data-id="element-1445">Other</option>
                  </select>
                  {unpublishReason === 'other' && <input type="text" value={otherReason} onChange={e => setOtherReason(e.target.value)} placeholder="Specify..." className="w-full px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none" data-id="element-1446" />}
                  <div className="flex gap-2" data-id="element-1447">
                    <button onClick={() => setShowUnpublish(false)} className="flex-1 py-2.5 bg-slate-100 text-slate-700 text-sm font-bold rounded-xl" data-id="element-1448">
                      Cancel
                    </button>
                    <button onClick={confirmUnpublish} className="flex-1 py-2.5 bg-slate-900 text-white text-sm font-bold rounded-xl" data-id="element-1449">
                      Confirm
                    </button>
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>

          <div className="h-px bg-slate-100" data-id="element-1450" />
          <div className="flex items-center justify-between" data-id="element-1451">
            <div className="font-semibold text-dark-navy text-sm" data-id="element-1452">
              Hide from Search
            </div>
            <button onClick={() => {
            setHideFromSearch(!hideFromSearch);
            notify(!hideFromSearch ? 'Hidden from search.' : 'Searchable.');
          }} data-id="element-1453">
              <ToggleRightIcon className={`w-10 h-10 transition-colors ${hideFromSearch ? 'text-primary-indigo' : 'text-slate-300 rotate-180'}`} data-id="element-1454" />
            </button>
          </div>
        </div>
      </section>

      <section data-id="element-1455">
        <h3 className="text-lg font-bold text-red-600 mb-3" data-id="element-1456">Danger Zone</h3>
        <div className="bg-red-50 border border-red-100 rounded-2xl p-4 flex flex-col gap-4" data-id="element-1457">
          <div className="flex items-center justify-between" data-id="element-1458">
            <div className="font-semibold text-red-900 text-sm" data-id="element-1459">
              {archived ? 'Restore Item' : 'Archive Item'}
            </div>
            <button onClick={toggleArchive} className="px-4 py-2 bg-white border border-red-200 text-red-700 text-sm font-bold rounded-xl active:bg-red-50" data-id="element-1460">
              {archived ? 'Restore' : 'Archive'}
            </button>
          </div>
          <div className="h-px bg-red-100" data-id="element-1461" />
          <div className="flex items-center justify-between" data-id="element-1462">
            <div className="font-semibold text-red-900 text-sm" data-id="element-1463">
              Delete Item
            </div>
            {confirmDelete ? <div className="flex items-center gap-2" data-id="element-1464">
                <button onClick={() => setConfirmDelete(false)} className="px-3 py-2 text-sm font-bold text-slate-600" data-id="element-1465">
                  Cancel
                </button>
                <button onClick={onClose} className="px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-xl" data-id="element-1466">
                  Confirm
                </button>
              </div> : <button onClick={() => setConfirmDelete(true)} className="px-4 py-2 bg-red-600 text-white text-sm font-bold rounded-xl active:bg-red-700" data-id="element-1467">
                Delete
              </button>}
          </div>
        </div>
      </section>
    </div>;
}
interface KnowledgeRule {
  id: number;
  type: string;
  cond: string;
  charge: string;
  active: boolean;
}
interface KnowledgeFaq {
  id: number;
  q: string;
  a: string;
  approved: boolean;
}
function AIRulesTab() {
  const [rules, setRules] = useState<KnowledgeRule[]>([{
    id: 1,
    type: 'Delivery Rule',
    cond: 'Dubai delivery',
    charge: 'AED 50',
    active: true
  }, {
    id: 2,
    type: 'Pickup Rule',
    cond: 'Dubai pickup',
    charge: 'AED 50',
    active: true
  }, {
    id: 3,
    type: 'Airport Rule',
    cond: 'Airport delivery',
    charge: 'AED 75',
    active: true
  }]);
  const [draft, setDraft] = useState('');
  const suggestions = ['Free delivery over 7 days', 'Add AED 100 for Sharjah', 'No smoking'];
  const buildRule = (text: string): KnowledgeRule => {
    const trimmed = text.trim();
    const chargeMatch = trimmed.match(/AED\s?[\d,]+/i);
    const durationMatch = trimmed.match(/\d+\s?days?/i);
    const charge = chargeMatch ? chargeMatch[0].toUpperCase().replace('AED', 'AED ').trim() : durationMatch ? durationMatch[0] : '—';
    let type = 'Custom Rule';
    if (trimmed.toLowerCase().includes('deliver')) type = 'Delivery Rule';
    return {
      id: Date.now(),
      type,
      cond: trimmed,
      charge,
      active: true
    };
  };
  const addRule = (text?: string) => {
    const value = (text ?? draft).trim();
    if (!value) return;
    setRules(prev => [buildRule(value), ...prev]);
    if (!text) setDraft('');
  };
  const toggleRule = (id: number) => setRules(prev => prev.map(r => r.id === id ? {
    ...r,
    active: !r.active
  } : r));
  const deleteRule = (id: number) => setRules(prev => prev.filter(r => r.id !== id));
  const [faqs, setFaqs] = useState<KnowledgeFaq[]>([{
    id: 101,
    q: 'Minimum rental period?',
    a: '2 days minimum.',
    approved: true
  }, {
    id: 102,
    q: 'Airport delivery?',
    a: 'Yes, for AED 75.',
    approved: true
  }]);
  const [addingFaq, setAddingFaq] = useState(false);
  const [draftQ, setDraftQ] = useState('');
  const [draftA, setDraftA] = useState('');
  const addFaq = () => {
    if (!draftQ.trim() || !draftA.trim()) return;
    setFaqs(prev => [{
      id: Date.now(),
      q: draftQ.trim(),
      a: draftA.trim(),
      approved: true
    }, ...prev]);
    setDraftQ('');
    setDraftA('');
    setAddingFaq(false);
  };
  return <div className="flex flex-col gap-8" data-id="element-1468">
      <section data-id="element-1469">
        <div className="flex items-center gap-2 mb-4" data-id="element-1470">
          <div className="w-8 h-8 rounded-lg bg-ai-purple/10 flex items-center justify-center" data-id="element-1471">
            <SparklesIcon className="w-4 h-4 text-ai-purple" data-id="element-1472" />
          </div>
          <h3 className="text-lg font-bold text-dark-navy" data-id="element-1473">Pricing Rules</h3>
        </div>

        <div className="mb-5" data-id="element-1474">
          <div className="relative" data-id="element-1475">
            <textarea rows={2} value={draft} onChange={e => setDraft(e.target.value)} placeholder='E.g. "Add AED 100 for Sharjah delivery"' className="w-full pl-4 pr-24 py-3 bg-purple-50/50 border border-purple-100 rounded-2xl focus:ring-2 focus:ring-ai-purple/30 outline-none text-sm resize-none" data-id="element-1476" />
            <button onClick={() => addRule()} disabled={!draft.trim()} className="absolute right-2 bottom-2 px-3 py-1.5 bg-ai-purple text-white text-xs font-bold rounded-lg active:scale-95 disabled:opacity-50" data-id="element-1477">
              Add
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3" data-id="element-1478">
            {suggestions.map(s => <button key={s} onClick={() => addRule(s)} className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 text-[11px] font-bold rounded-full active:bg-slate-50" data-id="element-1479">
                + {s}
              </button>)}
          </div>
        </div>

        <div className="flex flex-col gap-3" data-id="element-1480">
          {rules.map(rule => <div key={rule.id} className={`p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col gap-3 transition-opacity ${rule.active ? '' : 'opacity-60'}`} data-id="element-1481">
              <div className="flex justify-between items-start" data-id="element-1482">
                <div data-id="element-1483">
                  <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-1484">
                    {rule.type}
                  </div>
                  <div className="font-semibold text-dark-navy text-sm mt-0.5" data-id="element-1485">
                    {rule.cond}
                  </div>
                </div>
                <div className="font-bold text-primary-indigo text-sm" data-id="element-1486">
                  {rule.charge}
                </div>
              </div>
              <div className="flex items-center justify-between pt-3 border-t border-slate-100" data-id="element-1487">
                <button onClick={() => toggleRule(rule.id)} className={`px-3 py-1.5 text-xs font-bold rounded-lg flex items-center gap-1.5 ${rule.active ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-1488">
                  {rule.active ? <CheckCircle2Icon className="w-3.5 h-3.5" data-id="element-1489" /> : <ClockIcon className="w-3.5 h-3.5" data-id="element-1490" />}
                  {rule.active ? 'Active' : 'Paused'}
                </button>
                <button onClick={() => deleteRule(rule.id)} className="p-2 text-slate-400 active:text-red-600 active:bg-red-50 rounded-lg" data-id="element-1491">
                  <TrashIcon className="w-4 h-4" data-id="element-1492" />
                </button>
              </div>
            </div>)}
        </div>
      </section>

      <section data-id="element-1493">
        <div className="flex items-center justify-between mb-4" data-id="element-1494">
          <div className="flex items-center gap-2" data-id="element-1495">
            <div className="w-8 h-8 rounded-lg bg-primary-indigo/10 flex items-center justify-center" data-id="element-1496">
              <MessageSquareIcon className="w-4 h-4 text-primary-indigo" data-id="element-1497" />
            </div>
            <h3 className="text-lg font-bold text-dark-navy" data-id="element-1498">FAQs</h3>
          </div>
          <button onClick={() => setAddingFaq(!addingFaq)} className="px-3 py-1.5 bg-primary-indigo/10 text-primary-indigo text-xs font-bold rounded-lg active:bg-primary-indigo/20" data-id="element-1499">
            + Add FAQ
          </button>
        </div>

        <AnimatePresence data-id="element-1500">
          {addingFaq && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="overflow-hidden mb-4" data-id="element-1501">
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl flex flex-col gap-3" data-id="element-1502">
                <input type="text" value={draftQ} onChange={e => setDraftQ(e.target.value)} placeholder="Question..." className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm outline-none" data-id="element-1503" />
                <textarea rows={2} value={draftA} onChange={e => setDraftA(e.target.value)} placeholder="Answer..." className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm outline-none resize-none" data-id="element-1504" />
                <div className="flex gap-2" data-id="element-1505">
                  <button onClick={() => setAddingFaq(false)} className="flex-1 py-2.5 text-sm font-bold text-slate-600 bg-white border border-slate-200 rounded-xl" data-id="element-1506">
                    Cancel
                  </button>
                  <button onClick={addFaq} disabled={!draftQ || !draftA} className="flex-1 py-2.5 text-sm font-bold text-white bg-primary-indigo rounded-xl disabled:opacity-50" data-id="element-1507">
                    Save FAQ
                  </button>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        <div className="flex flex-col gap-3" data-id="element-1508">
          {faqs.map(faq => <div key={faq.id} className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col gap-2" data-id="element-1509">
              <div className="flex justify-between items-start gap-3" data-id="element-1510">
                <div className="font-bold text-dark-navy text-sm leading-snug" data-id="element-1511">
                  <span className="text-slate-400 mr-1" data-id="element-1512">Q:</span>
                  {faq.q}
                </div>
                <button onClick={() => setFaqs(faqs.filter(f => f.id !== faq.id))} className="p-1.5 text-slate-400 active:text-red-600 bg-slate-50 rounded-lg shrink-0" data-id="element-1513">
                  <TrashIcon className="w-4 h-4" data-id="element-1514" />
                </button>
              </div>
              <div className="text-sm text-slate-600 leading-relaxed" data-id="element-1515">
                <span className="text-ai-purple font-bold mr-1" data-id="element-1516">A:</span>
                {faq.a}
              </div>
            </div>)}
        </div>
      </section>
    </div>;
}